import React from 'react'
import { ReactComponent as LayoutGrid } from '../../../assets/svgs/layout-grid.svg'
import { ReactComponent as LayoutList } from '../../../assets/svgs/layout-list.svg'
import { ReactComponent as FileText } from '../../../assets/svgs/file-text.svg'
import { ReactComponent as CheckCheck } from '../../../assets/svgs/check-check.svg'
import { ReactComponent as MessageCircle } from '../../../assets/svgs/chat.svg'
import { ReactComponent as Calendar } from '../../../assets/svgs/calendar.svg'
import { ReactComponent as NetworkTree } from '../../../assets/svgs/network.svg'
import { ReactComponent as GraduationCap } from '../../../assets/svgs/training.svg'
import BarChartOutline from '../../../assets/svgs/barChartOutline'

/**
 * Sidebar icon family — one visual system replacing the previous mix of filled
 * Bootstrap glyphs, 16px Figma exports and 24px strokes.
 *
 * Every glyph lives on a 24x24 grid, stroke-only, round caps/joins, drawn inside
 * a 3-21 optical box. Rows that already had a matching 24-grid stroke asset in
 * `assets/svgs` reuse that file verbatim (re-exported below); the rest are drawn
 * here in the same language. Stroke weight is normalised by the wrapper in
 * sidebar-styles so the reused assets and these agree.
 */
const GRID = 24
const STROKE = 1.8

const IconBase = ({ size = 18, strokeWidth = STROKE, children, ...props }) => (
  <svg
    viewBox={`0 0 ${GRID} ${GRID}`}
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    {children}
  </svg>
)

/* ── Reused as-is from assets/svgs (already 24-grid strokes) ───────────── */

export const DashboardGlyph = LayoutGrid // Dashboard
export const DepartmentsBoardGlyph = LayoutList // Departments Board
export const DocumentGlyph = FileText // Job Requests
export const ChecklistGlyph = CheckCheck // Evaluation Forms
export const ChatGlyph = MessageCircle // Conversations
export const CalendarGlyph = Calendar // Calendar
export const BarChartGlyph = BarChartOutline // Reports
export const HierarchyGlyph = NetworkTree // Workforce Planning
export const GraduationCapGlyph = GraduationCap // Training Hub

/* ── Drawn here: no 24-grid stroke asset existed for these rows ────────── */

export const BuildingGlyph = (props) => (
  <IconBase {...props}>
    <path d="M4.8 20.6V5.4a1.8 1.8 0 0 1 1.8-1.8h6.6a1.8 1.8 0 0 1 1.8 1.8v15.2" />
    <path d="M15 10.4h2.6a1.8 1.8 0 0 1 1.8 1.8v8.4" />
    <path d="M3 20.6h18" />
    <path d="M8.3 7.4h3.4M8.3 11h3.4M8.3 14.6h3.4" />
  </IconBase>
)

export const SparkleGlyph = (props) => (
  <IconBase {...props}>
    <path d="M11.2 4.5l1.65 4.05 4.05 1.65-4.05 1.65L11.2 15.9l-1.65-4.05L5.5 10.2l4.05-1.65L11.2 4.5Z" />
    <path d="M18.2 14.4l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2Z" />
  </IconBase>
)

export const AssistinoGlyph = (props) => (
  <IconBase {...props}>
    <rect x="4" y="7.6" width="16" height="11.6" rx="3.2" />
    <path d="M12 4v3.6" />
    <path d="M9.4 12.4v1.6" />
    <path d="M14.6 12.4v1.6" />
    <path d="M9.6 16.8h4.8" />
  </IconBase>
)

export const ClipboardCheckGlyph = (props) => (
  <IconBase {...props}>
    <rect x="9" y="2.8" width="6" height="3.6" rx="1.4" />
    <path d="M9 4.6H7.6a1.8 1.8 0 0 0-1.8 1.8v12.6a1.8 1.8 0 0 0 1.8 1.8h8.8a1.8 1.8 0 0 0 1.8-1.8V6.4a1.8 1.8 0 0 0-1.8-1.8H15" />
    <path d="M9.3 13.5 11.1 15.3l3.7-4.2" />
  </IconBase>
)

export const BriefcaseGlyph = (props) => (
  <IconBase {...props}>
    <rect x="3" y="7.4" width="18" height="12.8" rx="2.6" />
    <path d="M8.8 7.4V5.8A1.8 1.8 0 0 1 10.6 4h2.8a1.8 1.8 0 0 1 1.8 1.8v1.6" />
    <path d="M3 12.6h18" />
  </IconBase>
)

export const UsersGlyph = (props) => (
  <IconBase {...props}>
    <circle cx="9.4" cy="8" r="3.5" />
    <path d="M3.3 20.4v-1.1a4.6 4.6 0 0 1 4.6-4.6h3a4.6 4.6 0 0 1 4.6 4.6v1.1" />
    <path d="M15.5 4.8a3.5 3.5 0 0 1 0 6.4" />
    <path d="M17.5 14.9a4.6 4.6 0 0 1 3.2 4.4v1.1" />
  </IconBase>
)

export const EnvelopeGlyph = (props) => (
  <IconBase {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2.4" />
    <path d="M3.9 6.7 10.7 12a2.1 2.1 0 0 0 2.6 0l6.8-5.3" />
  </IconBase>
)

export const GearGlyph = (props) => (
  <IconBase {...props}>
    <path d="M9.46 6.02 10.19 3.49 13.81 3.49 14.54 6.02A6.5 6.5 0 0 1 15.91 6.81L18.47 6.18 20.27 9.31 18.45 11.21A6.5 6.5 0 0 1 18.45 12.79L20.27 14.69 18.47 17.82 15.91 17.19A6.5 6.5 0 0 1 14.54 17.98L13.81 20.51 10.19 20.51 9.46 17.98A6.5 6.5 0 0 1 8.09 17.19L5.53 17.82 3.73 14.69 5.55 12.79A6.5 6.5 0 0 1 5.55 11.21L3.73 9.31 5.53 6.18 8.09 6.81A6.5 6.5 0 0 1 9.46 6.02Z" />
    <circle cx="12" cy="12" r="3.1" />
  </IconBase>
)

export const DatabaseGlyph = (props) => (
  <IconBase {...props}>
    <ellipse cx="12" cy="6.2" rx="7.6" ry="3.2" />
    <path d="M4.4 6.2v5.8c0 1.77 3.4 3.2 7.6 3.2s7.6-1.43 7.6-3.2V6.2" />
    <path d="M4.4 12v5.8c0 1.77 3.4 3.2 7.6 3.2s7.6-1.43 7.6-3.2V12" />
  </IconBase>
)

export const IdCardGlyph = (props) => (
  <IconBase {...props}>
    <rect x="2.6" y="4.8" width="18.8" height="14.4" rx="2.6" />
    <circle cx="8.6" cy="11" r="2.2" />
    <path d="M5.4 16.5a3.7 3.7 0 0 1 6.4 0" />
    <path d="M14.8 10.2h4M14.8 13.8h4" />
  </IconBase>
)

export const BookGlyph = (props) => (
  <IconBase {...props}>
    <path d="M12 6.7C10.6 5.1 8.2 4.5 4.4 4.5a1.1 1.1 0 0 0-1.1 1.1v11.2a1.1 1.1 0 0 0 1.1 1.1c3.8 0 6.2.6 7.6 2.2 1.4-1.6 3.8-2.2 7.6-2.2a1.1 1.1 0 0 0 1.1-1.1V5.6a1.1 1.1 0 0 0-1.1-1.1c-3.8 0-6.2.6-7.6 2.2Z" />
    <path d="M12 6.7v13.4" />
  </IconBase>
)

export const NetworkGlyph = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="5.4" r="2.6" />
    <circle cx="5.6" cy="18.4" r="2.6" />
    <circle cx="18.4" cy="18.4" r="2.6" />
    <path d="M10.1 7.2 7.2 16.1" />
    <path d="M13.9 7.2l2.9 8.9" />
    <path d="M8.2 18.4h7.6" />
  </IconBase>
)

export const FunnelGlyph = (props) => (
  <IconBase {...props}>
    <path d="M3.6 5.2h16.8l-6.6 7.8v6.2l-3.6 1.8v-8L3.6 5.2Z" />
  </IconBase>
)

export const ClockGlyph = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="12" r="8.6" />
    <path d="M12 7.2V12l3.4 2.1" />
  </IconBase>
)

export const AwardGlyph = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="9.2" r="5.6" />
    <path d="M8.4 13.8 7 21l5-2.7 5 2.7-1.4-7.2" />
  </IconBase>
)

export const GaugeGlyph = (props) => (
  <IconBase {...props}>
    <path d="M3.4 17.8a9 9 0 1 1 17.2 0" />
    <path d="M12.6 14.2 16 9.6" />
    <circle cx="12" cy="15.2" r="1.4" />
  </IconBase>
)

export const MicGlyph = (props) => (
  <IconBase {...props}>
    <rect x="9.2" y="2.8" width="5.6" height="10.4" rx="2.8" />
    <path d="M5.8 11.2a6.2 6.2 0 0 0 12.4 0" />
    <path d="M12 17.4v3.4" />
    <path d="M8.8 20.8h6.4" />
  </IconBase>
)

export const PercentGlyph = (props) => (
  <IconBase {...props}>
    <path d="M18.6 5.4 5.4 18.6" />
    <circle cx="7.6" cy="7.6" r="2.6" />
    <circle cx="16.4" cy="16.4" r="2.6" />
  </IconBase>
)

export const MoneyGlyph = (props) => (
  <IconBase {...props}>
    <rect x="2.6" y="6.4" width="18.8" height="11.2" rx="2.4" />
    <circle cx="12" cy="12" r="2.6" />
    <path d="M6.2 12h.01M17.8 12h.01" />
  </IconBase>
)

export const HourglassGlyph = (props) => (
  <IconBase {...props}>
    <path d="M6.4 3.2h11.2" />
    <path d="M6.4 20.8h11.2" />
    <path d="M17 3.2c0 4.3-5 4.5-5 8.8s5 4.5 5 8.8" />
    <path d="M7 3.2c0 4.3 5 4.5 5 8.8s-5 4.5-5 8.8" />
  </IconBase>
)

export const StopwatchGlyph = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="13.6" r="7.2" />
    <path d="M12 10.6v3.2l2.2 1.5" />
    <path d="M12 3.2v3.2" />
    <path d="M9.8 3.2h4.4" />
  </IconBase>
)

export const UserCheckGlyph = (props) => (
  <IconBase {...props}>
    <circle cx="9.2" cy="7.8" r="3.8" />
    <path d="M2.8 20.6v-1.6a4.4 4.4 0 0 1 4.4-4.4h3.4a4.4 4.4 0 0 1 4.4 4.4v1.6" />
    <path d="M16 11.4 17.9 13.3 21 10.2" />
  </IconBase>
)

export const UserSearchGlyph = (props) => (
  <IconBase {...props}>
    <circle cx="9.6" cy="7.6" r="3.8" />
    <path d="M3.2 20.6v-1.6a4.4 4.4 0 0 1 4.4-4.4h2.8" />
    <circle cx="16.4" cy="16.2" r="3.4" />
    <path d="M18.9 18.7 21 20.8" />
  </IconBase>
)

export const PieChartGlyph = (props) => (
  <IconBase {...props}>
    <path d="M20.6 15.6A9 9 0 1 1 8.4 3.4" />
    <path d="M21 12A9 9 0 0 0 12 3v9h9Z" />
  </IconBase>
)

export const EnvelopeCheckGlyph = (props) => (
  <IconBase {...props}>
    <path d="M20.4 11.6V7.4A2.4 2.4 0 0 0 18 5H5.4A2.4 2.4 0 0 0 3 7.4v9.2A2.4 2.4 0 0 0 5.4 19h6.2" />
    <path d="M3.9 8 10.7 12.6a2.1 2.1 0 0 0 2.6 0L20.1 8" />
    <path d="M15.2 17.2 17.4 19.4 21 15.6" />
  </IconBase>
)

export const UserXGlyph = (props) => (
  <IconBase {...props}>
    <circle cx="8.4" cy="7.8" r="3.8" />
    <path d="M14.6 20.6v-1.6a4.4 4.4 0 0 0-4.4-4.4H6.6a4.4 4.4 0 0 0-4.4 4.4v1.6" />
    <path d="M16.8 8.6 21 12.8" />
    <path d="M21 8.6 16.8 12.8" />
  </IconBase>
)

export const CalculatorGlyph = (props) => (
  <IconBase {...props}>
    <rect x="4.4" y="2.8" width="15.2" height="18.4" rx="2.4" />
    <rect x="7.6" y="6" width="8.8" height="3.4" rx="1" />
    <path d="M8.4 13h.01M12 13h.01M15.6 13h.01" />
    <path d="M8.4 17h.01M12 17h.01M15.6 17h.01" />
  </IconBase>
)

export const WalletGlyph = (props) => (
  <IconBase {...props}>
    <rect x="2.8" y="5.2" width="18.4" height="13.6" rx="2.6" />
    <path d="M21.2 10.2h-5a2.4 2.4 0 0 0 0 4.8h5" />
  </IconBase>
)

export const ChevronDownGlyph = (props) => (
  <IconBase size={12} strokeWidth={2.2} {...props}>
    <path d="M5.5 9.5 12 16l6.5-6.5" />
  </IconBase>
)

export default IconBase
